import {
  Box,
  CircularProgress,
  TextField,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import { addDays, format } from "date-fns";
import React, { useEffect, useState } from "react";
import { API_RESULT, API_VERSION } from "../../../../enums/common.enum";
import { instance } from "../../../../utils/api";
import {
  amountConverter,
  getMuiTheme,
  handleError,
} from "../../../../utils/common";
import MUIDataTable from "mui-datatables";
import { commonTableOptions } from "../../../../constants";
import DynamicFeedIcon from "@mui/icons-material/DynamicFeed";
import { ThemeProvider } from "@mui/styles";
import { useParams } from "react-router-dom";
import MerchantWiseTxnModal from "./MerchantWiseTxnModal";
import { DateRangePicker } from "mui-daterange-picker";
import { dateRangeMenu } from "../../../../utils/dateRangeMenu";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { AuthenticatedUser } from "../../../../interfaces/user.model";
import useUserInfo from "../../../../hooks/useUserInfo";

const MerchantTransactions = ({
  providerInfo,
  dateRangeTxn,
  setDateRangeTxn,
  data,
  setData,
  visitedTabs,
  updateVisitedTabs,
}: any) => {
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [showTxnModal, setShowTxnModal] = useState(false);
  const params = useParams();
  const [openPicker, setOpenPicker] = useState(false);
  const user: AuthenticatedUser = useUserInfo();
  const canDownloadPDF =
    user?.role === "manager" || user?.role === "super admin";

  const toggle = () => setOpenPicker(!openPicker);

  useEffect(() => {
    const getTxnData = async () => {
      let payload = {
        start_date: format(dateRangeTxn?.startDate as Date, "yyyy-MM-dd"),
        end_date: format(dateRangeTxn?.endDate as Date, "yyyy-MM-dd"),
        metric_type: "pharmacy_transactions_group",
        location_id: params?.locationId,
      };
      //setData([]); //reset table data on date change
      setLoading(true);
      try {
        const result = await instance.post(
          `${API_VERSION.V2}/analytics/digital-payments/overview`,
          payload,
        );
        if (result.data?.status === API_RESULT.SUCCESS) {
          updateVisitedTabs(2, "add");
          setData(result.data.response?.transactions);
          setOpenPicker(false);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };
    if (!visitedTabs.includes(2)) {
      getTxnData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeTxn]);

  const downloadPDF = () => {
    const doc = new jsPDF("landscape", "mm", "a4");

    // ---------------------------------------------------------------------------
    // Pharmacy / Statement Details
    // ---------------------------------------------------------------------------

    const pharmacy = {
      name: providerInfo?.provider_name ?? "-",

      address: providerInfo?.formatted_address || providerInfo?.address || "-",

      phone: providerInfo?.whatsapp_number ?? "-",

      bankName: providerInfo?.bank_name ?? "-",

      accountNumber: providerInfo?.account_no
        ? `XXXX XXXX ${String(providerInfo.account_no).slice(-4)}`
        : "-",

      ifsc: providerInfo?.ifsc_code ?? "-",

      branch: providerInfo?.branch_name ?? "-",

      gst: providerInfo?.gst_no || "-",

      pan: providerInfo?.pan_no || "-",
    };

    // ---------------------------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------------------------

    const formatDate = (date: Date | string | undefined | null) => {
      if (!date) return "-";

      const parsedDate = new Date(date);

      if (Number.isNaN(parsedDate.getTime())) {
        return "-";
      }

      return format(parsedDate, "dd-MMM-yyyy");
    };

    const formatCurrency = (value: number) => {
      return `Rs. ${value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };

    const drawHorizontalLine = (y: number) => {
      doc.setDrawColor(210, 210, 210);
      doc.setLineWidth(0.3);
      doc.line(10, y, 287, y);
    };

    // ---------------------------------------------------------------------------
    // Downloadable Columns
    // ---------------------------------------------------------------------------

    const pdfColumns = cols.filter(
      (column: any) => column.options?.download !== false,
    );

    const headers = pdfColumns.map((column: any) => column.label);

    const rows = data.map((item: any) => {
      return pdfColumns.map((column: any) => {
        switch (column.name) {
          case "date":
            return formatDate(item?.date);

          case "transaction_count":
            return item?.transaction_count ?? "-";

          case "transaction_value":
            return item?.transaction_value ?? "-";

          default:
            return item?.[column.name] ?? "-";
        }
      });
    });

    // ---------------------------------------------------------------------------
    // Totals
    // ---------------------------------------------------------------------------

    const totalCount = data.reduce(
      (acc: number, curr: any) => acc + Number(curr?.transaction_count || 0),
      0,
    );

    const totalAmount = data.reduce(
      (acc: number, curr: any) => acc + Number(curr?.transaction_value || 0),
      0,
    );

    // ---------------------------------------------------------------------------
    // Page Configuration
    // ---------------------------------------------------------------------------

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const leftMargin = 10;
    const rightMargin = 10;

    // ---------------------------------------------------------------------------
    // Header
    // ---------------------------------------------------------------------------

    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);

    doc.text(pharmacy.name, leftMargin, 14);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);

    // Address
    const addressLines = doc.splitTextToSize(pharmacy.address, 190);

    doc.text(addressLines, leftMargin, 20);

    // Phone
    const phoneY = 20 + addressLines.length * 4.5;

    doc.text(`Phone: ${pharmacy.phone}`, leftMargin, phoneY);

    // ---------------------------------------------------------------------------
    // Statement Title
    // ---------------------------------------------------------------------------

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);

    doc.text("TRANSACTION STATEMENT", pageWidth - rightMargin, 14, {
      align: "right",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);

    doc.text(
      `Statement Tenure: ${formatDate(
        dateRangeTxn?.startDate,
      )} - ${formatDate(dateRangeTxn?.endDate)}`,
      pageWidth - rightMargin,
      20,
      {
        align: "right",
      },
    );

    // ---------------------------------------------------------------------------
    // Divider
    // ---------------------------------------------------------------------------

    const headerBottomY = Math.max(phoneY + 5, 27);

    drawHorizontalLine(headerBottomY);

    // ---------------------------------------------------------------------------
    // Pharmacy / Bank / Tax Details
    // ---------------------------------------------------------------------------

    const detailsTopY = headerBottomY + 7;

    doc.setFontSize(9);

    // Section titles
    doc.setFont("helvetica", "bold");

    doc.text("Pharmacy Details", leftMargin, detailsTopY);

    doc.text("Bank Details", 105, detailsTopY);

    doc.text("Tax Details", 205, detailsTopY);

    // Details
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    const detailStartY = detailsTopY + 5;
    const detailLineHeight = 4.5;

    // Pharmacy details
    doc.text(`Phone: ${pharmacy.phone}`, leftMargin, detailStartY);

    // Bank details
    doc.text(`Bank: ${pharmacy.bankName}`, 105, detailStartY);

    doc.text(
      `A/C No.: ${pharmacy.accountNumber}`,
      105,
      detailStartY + detailLineHeight,
    );

    doc.text(
      `IFSC: ${pharmacy.ifsc}`,
      105,
      detailStartY + detailLineHeight * 2,
    );

    doc.text(
      `Branch: ${pharmacy.branch}`,
      105,
      detailStartY + detailLineHeight * 3,
    );

    // Tax details
    doc.text(`GSTIN: ${pharmacy.gst}`, 205, detailStartY);

    doc.text(`PAN: ${pharmacy.pan}`, 205, detailStartY + detailLineHeight);

    // ---------------------------------------------------------------------------
    // Summary
    // ---------------------------------------------------------------------------

    const summaryTopY = detailStartY + 24;

    drawHorizontalLine(summaryTopY);

    const summaryY = summaryTopY + 7;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);

    doc.text("Statement Summary", leftMargin, summaryY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);

    doc.text(
      `Transaction Count: ${totalCount.toLocaleString("en-IN")}`,
      80,
      summaryY,
    );

    doc.text(`Total Amount: ${formatCurrency(totalAmount)}`, 180, summaryY);

    // ---------------------------------------------------------------------------
    // Transaction Table
    // ---------------------------------------------------------------------------

    const tableStartY = summaryY + 7;

    autoTable(doc, {
      head: [headers],
      body: rows,

      startY: tableStartY,

      theme: "grid",

      margin: {
        top: 10,
        left: leftMargin,
        right: rightMargin,
        bottom: 30,
      },

      styles: {
        font: "helvetica",
        fontSize: 7.5,
        cellPadding: 2.5,
        overflow: "linebreak",
        valign: "middle",
        lineColor: [210, 210, 210],
        lineWidth: 0.2,
      },

      headStyles: {
        font: "helvetica",
        fontStyle: "bold",
        fontSize: 7.5,
        valign: "middle",
        halign: "center",
        cellPadding: 2.5,
      },

      bodyStyles: {
        fontSize: 7.5,
      },

      alternateRowStyles: {
        // Keeps the statement easy to scan without being visually heavy.
        fillColor: [248, 248, 248],
      },

      columnStyles: {
        // Optional alignment for common numeric columns.
        // These indexes are calculated dynamically below.
      },

      didParseCell: (hookData: any) => {
        // Right-align numeric transaction columns.
        if (hookData.section === "body" || hookData.section === "head") {
          const columnName = pdfColumns[hookData.column.index]?.name;

          if (
            columnName === "transaction_count" ||
            columnName === "transaction_value"
          ) {
            hookData.cell.styles.halign = "right";
          }
        }
      },

      didDrawPage: () => {
        // -----------------------------------------------------------------------
        // Footer
        // -----------------------------------------------------------------------

        const footerY = pageHeight - 10;

        drawHorizontalLine(footerY - 4);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);

        doc.text(
          `Generated on ${format(new Date(), "dd-MMM-yyyy HH:mm")}`,
          leftMargin,
          footerY,
        );

        doc.text(
          `Page ${doc.getNumberOfPages()}`,
          pageWidth - rightMargin,
          footerY,
          {
            align: "right",
          },
        );
      },
    });

    // ---------------------------------------------------------------------------
    // Notes
    // ---------------------------------------------------------------------------

    // autoTable exposes the final Y position through lastAutoTable.
    const lastTableY = (doc as any).lastAutoTable?.finalY ?? tableStartY;

    let notesY = lastTableY + 10;

    // If notes won't comfortably fit, start a new page.
    if (notesY > pageHeight - 40) {
      doc.addPage();
      notesY = 18;
    }

    drawHorizontalLine(notesY - 4);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);

    doc.text("Notes", leftMargin, notesY + 2);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    const notes = [
      "This is a system generated statement and does not require a signature.",
      "All amounts are in Indian Rupees (INR).",
      "For any queries, please contact our support team.",
    ];

    let currentNoteY = notesY + 8;

    notes.forEach((note, index) => {
      const noteText = `${index + 1}. ${note}`;

      const noteLines = doc.splitTextToSize(
        noteText,
        pageWidth - leftMargin - rightMargin,
      );

      doc.text(noteLines, leftMargin, currentNoteY);

      currentNoteY += noteLines.length * 4 + 1;
    });

    // ---------------------------------------------------------------------------
    // File Name
    // ---------------------------------------------------------------------------

    const startDate = dateRangeTxn?.startDate
      ? format(new Date(dateRangeTxn.startDate), "dd-MMM-yyyy")
      : "Statement";

    const endDate = dateRangeTxn?.endDate
      ? format(new Date(dateRangeTxn.endDate), "dd-MMM-yyyy")
      : "";

    const fileName = `Transaction_Statement_${startDate}_${endDate}.pdf`;

    doc.save(fileName);
  };

  const cols = [
    {
      name: "date",
      label: "Date",
      options: {
        sort: false,
        customBodyRender: (value: any) => {
          return (
            <Typography variant="body2">
              {format(new Date(value), "dd/MM/yyyy")}
            </Typography>
          );
        },
      },
    },
    {
      name: "transaction_count",
      label: "Txn Count",
      options: {
        searchable: false,
      },
    },
    {
      name: "transaction_value",
      label: "Value(Rs.)",
      options: {
        searchable: false,
        sort: true,
      },
    },
    {
      name: "date",
      label: "All Transactions",
      options: {
        download: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any) => {
          return (
            <Stack direction="row" alignItems="center">
              <Stack
                onClick={() => {
                  setSelectedDate(new Date(value));
                  setShowTxnModal(true);
                }}
                direction="row"
                alignItems="center"
                sx={{
                  cursor: "pointer",
                }}
              >
                <DynamicFeedIcon color="primary" />
                <Typography color="primary" sx={{ pl: 0.5 }} variant="body2">
                  View
                </Typography>
              </Stack>
            </Stack>
          );
        },
      },
    },
  ];

  const options: any = {
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress sx={{ color: "#0058ff" }} />
        ) : (
          "Sorry, there is no matching data to display"
        ),
      },
    },
    ...commonTableOptions,
    searchPlaceholder: "Terminal ID",
    sort: true,
    filter: false,
    rowsPerPageOptions: [10, 50, 100, 200],
    rowsPerPage: 10,
  };

  return (
    <div>
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Stack>
          <TextField
            sx={{ minWidth: "275px" }}
            fullWidth
            id="date_range"
            label={dateRangeTxn?.label}
            size="small"
            value={`${format(
              dateRangeTxn?.startDate as Date,
              "dd/MM/yyyy",
            )} - ${format(dateRangeTxn?.endDate as Date, "dd/MM/yyyy")}`}
            onFocus={toggle}
          />
          <Box sx={{ position: "absolute", zIndex: 1000 }}>
            <DateRangePicker
              open={openPicker}
              toggle={toggle}
              onChange={(range) => {
                updateVisitedTabs(2, "remove");
                setDateRangeTxn(range);
              }}
              minDate={"2024-04-01"}
              maxDate={addDays(new Date(), 1)}
              definedRanges={dateRangeMenu}
            />
          </Box>
        </Stack>
        <Box
          width={{ xs: "100%", md: "30%" }}
          sx={{
            boxShadow: "0 0 .25rem rgba(0,0,0,.075)",
            padding: "10px",
            borderRadius: "5px",
            display: "flex",
          }}
        >
          <Box sx={{ width: "50%", textAlign: "center" }} pt={1}>
            <Typography
              sx={{
                fontSize: "12px !important",
              }}
            >
              Count
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "20px !important" }}>
              <strong>
                {data?.length > 0
                  ? data?.reduce(
                      (acc: number, curr: any) =>
                        acc + Number(curr.transaction_count),
                      0,
                    )
                  : 0}
              </strong>
            </Typography>
          </Box>
          <Box
            sx={{
              width: "50%",
              borderLeft: "1px solid #e7e7e7",
              textAlign: "center",
            }}
            pt={1}
          >
            <Typography
              sx={{
                fontSize: "12px !important",
              }}
            >
              Amount
            </Typography>
            <Typography variant="h6" sx={{ fontSize: "20px !important" }}>
              &#8377;&nbsp;
              {amountConverter(
                data?.reduce(
                  (acc: number, curr: any) =>
                    acc + Number(curr.transaction_value),
                  0,
                ),
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box mt={3}>
        {canDownloadPDF && (
          <Stack direction="row" justifyContent="flex-end" mb={1}>
            <Button variant="outlined" size="small" onClick={downloadPDF}>
              Download PDF
            </Button>
          </Stack>
        )}

        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable title="" data={data} columns={cols} options={options} />
        </ThemeProvider>
      </Box>
      {showTxnModal ? (
        <MerchantWiseTxnModal
          date={selectedDate}
          selected={providerInfo}
          showTxnModal={showTxnModal}
          setShowTxnModal={setShowTxnModal}
        />
      ) : null}
    </div>
  );
};

export default MerchantTransactions;
